// SNOWCLUB-V101-GA4-DESIGN-SEARCH-ITEM-20261002-002 — GA4 `search`, `view_item_list` and `view_item` for the design
// landing's own Search, the booking results it opens, and the marketplace product Details it expands.
//
// SEARCH ACROSS A HARD NAVIGATION. The design's Search (lSearchTransport → sgFunnelNav) sets `window.location.href`
// straight after the click, so a gtag hit fired on the landing races the unload and can be lost. The design therefore
// ANNOUNCES the search (`{ type: "sg:ga", event: "search", term, nav }`) and the host:
//   - nav === false (the in-page path, no navigation): sends `search` now;
//   - nav === true: writes ONE durable, non-PII handoff to sessionStorage — the zone-pair slug and a timestamp, nothing
//     else — and the booking results page consumes it on mount: read, delete, send. Consume-and-delete makes it exactly
//     once: a reload, Back/Forward or a second results render finds nothing.
// The handoff is written only when the gates are open at the click (production host + tag present),
// so staging or pre-init activity leaves nothing behind; the gates are checked again when it is sent.
//
// Every send goes through @/lib/analytics-event, so the production-host and initialized-tag gates are the same as every
// other event on the site.
import { onAnalyticsRecheck } from "@/lib/analytics-init";
import { analyticsGate, sendAnalyticsEvent, trackSearch, trackViewItem } from "@/lib/analytics-event";
import type { AnalyticsEventOutcome, GaItem } from "@/lib/analytics-event";

export const SEARCH_HANDOFF_KEY = "sg.ga4.search_handoff";
/** A handoff older than this was never followed by its results page (navigation failed, tab closed): drop it. */
const HANDOFF_TTL_MS = 5 * 60 * 1000;
/** The term is a served route slug (`vancouver-to-whistler`): lowercase zone words joined by `-to-`, never typed text. */
const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*-to-[a-z0-9]+(?:-[a-z0-9]+)*$/;

const cleanTerm = (raw: unknown): string | null => {
  if (typeof raw !== "string") return null;
  const t = raw.trim().toLowerCase();
  return t.length <= 100 && SLUG.test(t) ? t : null;
};

/** The design announced a search. Returns what happened, for tests and for nothing else. */
export function recordDesignSearch(rawTerm: unknown, navigating: boolean): AnalyticsEventOutcome | "empty" | "handoff" | "invalid" | "gated" {
  const term = cleanTerm(rawTerm);
  if (!term) return "invalid";
  if (!navigating) return trackSearch(term);
  if (analyticsGate() !== "open") return "gated";
  try {
    window.sessionStorage.setItem(SEARCH_HANDOFF_KEY, JSON.stringify({ term, at: Date.now() }));
  } catch {
    return "gated"; // storage blocked: no durable handoff is possible, and a racing send would be a guess
  }
  return "handoff";
}

/** The booking results page mounted for `slug`. Sends the pending search once, if it was for this route.
 *  `pending` means the handoff is valid but gtag does not exist yet (the results page can mount before analytics
 *  initialises): it is left in storage, not consumed, so a later retry can still send it. */
export function consumeSearchHandoff(
  slug: string,
  now = Date.now(),
): AnalyticsEventOutcome | "empty" | "none" | "stale" | "mismatch" | "pending" {
  if (typeof window === "undefined") return "none";
  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(SEARCH_HANDOFF_KEY);
  } catch {
    return "none";
  }
  if (raw === null) return "none";
  const consume = () => {
    try {
      window.sessionStorage.removeItem(SEARCH_HANDOFF_KEY);
    } catch {
      /* nothing more can be done */
    }
  };
  let parsed: { term?: unknown; at?: unknown } | null = null;
  try {
    parsed = JSON.parse(raw) as { term?: unknown; at?: unknown } | null;
  } catch {
    consume();
    return "none";
  }
  const term = cleanTerm(parsed?.term);
  const at = typeof parsed?.at === "number" ? parsed.at : NaN;
  if (!term || !Number.isFinite(at) || now - at > HANDOFF_TTL_MS || now < at) {
    consume();
    return "stale";
  }
  if (term !== slug) {
    consume();
    return "mismatch";
  }
  if (analyticsGate() === "tag-not-initialised") return "pending";
  consume(); // decided now: sent, or a gate (host, consent) is closed and the search is not measured
  return trackSearch(term);
}

/** Consume the handoff now and, while it is still pending on tag initialisation, again on each recheck signal.
 *  Returns the unsubscribe (call it on unmount). */
export function watchSearchHandoff(slug: string): () => void {
  let off: () => void = () => undefined;
  const attempt = () => {
    if (consumeSearchHandoff(slug) !== "pending") off();
  };
  off = onAnalyticsRecheck(attempt);
  attempt();
  return off;
}

// ── view_item_list ──────────────────────────────────────────────────────────────────────────────────
// Once per distinct displayed list per page load: a re-render, a filter toggle back to the same rows, or React's
// strict-mode double effect does not repeat it. An empty list is never reported.
const listsSeen = new Set<string>();

export function trackViewItemList(listId: string, listName: string, items: GaItem[]): AnalyticsEventOutcome | "empty" | "duplicate" {
  if (items.length === 0) return "empty";
  const fingerprint = listId + "|" + items.map((i) => i.item_id).join(",");
  if (listsSeen.has(fingerprint)) return "duplicate";
  const indexed = items.map((i, index) => ({ ...i, index, item_list_id: listId, item_list_name: listName }));
  const outcome = sendAnalyticsEvent("view_item_list", { item_list_id: listId, item_list_name: listName, items: indexed });
  if (outcome === "sent") listsSeen.add(fingerprint); // a closed gate counted nothing, so a later grant still reports
  return outcome;
}

/** Report a displayed list; if consent or the tag was not ready, report it once when analytics rechecks (a visitor
 *  who accepts the banner on already-displayed results). Returns the unsubscribe (call it on cleanup). */
export function watchViewItemList(listId: string, listName: string, items: GaItem[]): () => void {
  let off: () => void = () => undefined;
  const attempt = () => {
    const outcome = trackViewItemList(listId, listName, items);
    if (outcome !== "tag-not-initialised") off();
  };
  off = onAnalyticsRecheck(attempt);
  attempt();
  return off;
}

/** A ride row of the board the design paints (`hostedRidesFromResult`), reduced to what GA4 items need. The rows are
 *  the design's own displayed list: a departure with no fare was dropped there, so it is not reported here either. */
export interface DisplayedRide { sgSid?: unknown; op?: unknown; guest?: unknown; member?: unknown }

const fare = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null);

/** GA4 items for the rides the design shows. `member` selects the fare the signed-in design prices from; a ride with no
 *  engine id cannot be told apart from another, so it is skipped rather than given an invented one. */
export function gaItemsFromDisplayedRides(rides: readonly DisplayedRide[], leg: "depart" | "return", member: boolean): GaItem[] {
  const items: GaItem[] = [];
  for (const r of rides) {
    const id = typeof r.sgSid === "string" || typeof r.sgSid === "number" ? String(r.sgSid).trim() : "";
    const price = fare(member ? r.member : r.guest);
    // A visible card can offer only one side of a fare. Do not turn the unavailable side into a
    // made-up $0 GA4 price; the design's current session decides which real price is displayed.
    if (!id || price === null) continue;
    const brand = typeof r.op === "string" && r.op.trim() ? r.op.trim().slice(0, 100) : undefined;
    items.push({
      item_id: id,
      item_name: leg === "depart" ? "BUS SEAT (Departure)" : "BUS SEAT (Return)",
      ...(brand ? { item_brand: brand } : {}),
      item_category: "transport",
      quantity: 1,
      price,
    });
  }
  return items;
}

// ── design messages ─────────────────────────────────────────────────────────────────────────────────
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null);
const str = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim().slice(0, 100) : undefined);

/** Handle one `sg:ga` message from the design bundle. Only whitelisted fields are read. */
export function handleDesignAnalyticsMessage(data: unknown): string {
  const d = data as Record<string, unknown> | null;
  if (d?.type !== "sg:ga") return "ignored";
  if (d.event === "search") return recordDesignSearch(d.term, d.nav === true);
  if (d.event === "view_item") {
    const id = str(d.id);
    const name = str(d.name);
    if (!id || !name) return "invalid";
    const brand = str(d.vendor);
    const category = str(d.cat);
    return trackViewItem({
      item_id: id,
      item_name: name,
      quantity: 1,
      price: num(d.price) ?? 0,
      ...(brand ? { item_brand: brand } : {}),
      ...(category ? { item_category: category.toLowerCase() } : {}),
    });
  }
  return "ignored";
}

/** Test-only. */
export function _resetDesignSearchForTests(): void {
  listsSeen.clear();
  try {
    window.sessionStorage.removeItem(SEARCH_HANDOFF_KEY);
  } catch {
    /* nothing to clear */
  }
}
